@if (loading || (!loading && noData)) {
  <div class="message-container">
    @if (loading) {
      <div class="loading-wrapper">
        <i class="loading-icon fas fa-spinner"></i>
        <pre>Loading...</pre>
      </div>
    }
    @if (!loading && noData) {
      <div>
        <pre [style.font-size]="'large'">Feature not available for this project...</pre>
      </div>
    }
  </div>
}

<div class='contents-container' (click)="clearCheckedContent()">
  @if (!loading && !noData && activeDirectory) {
    @if (activeDirectory.contents) {
      <ng-container *ngTemplateOutlet="viewMode === 'TABLE' ? tableView : iconView"></ng-container>
    }
    @if (activeDirectory.loading) {
      <div class="loading-row"><i class="fas fa-spinner spinning"></i>Loading...</div>
    }
    @if (activeDirectory.error) {
      <div class="load-error">
        <span>This folder took too long to load.</span>
        <button class="btn btn-sm btn-outline-primary" (click)="loadMore(); $event.stopPropagation()">Retry</button>
        <a class="btn btn-sm btn-link" [routerLink]="['/dashboard/filesearch']" [state]="{ directory: activeDirectory }">Search this folder in File Search</a>
      </div>
    }
    @if (activeDirectory.contents) {
      @let loaded = activeDirectory.contents.length;
      @let total = activeDirectory.total ?? loaded;
      <div class="status-line">
        <span>Showing {{ loaded | number }} of {{ total | number }}</span>
        <button class="btn btn-sm btn-link" [disabled]="loaded === 0 || checkedContent.length === loaded" (click)="checkAll(); $event.stopPropagation()">Select all loaded ({{ loaded | number }} of {{ total | number }})</button>
      </div>
    }
  }
</div>

<!-- Icon view for contents, in rows of tiles -->
<ng-template #iconView>
  <cdk-virtual-scroll-viewport #gridViewport class="viewport" [itemSize]="tileRowHeight"
    (scrolledIndexChange)="onScrolledIndexChange($event, gridViewport.getViewportSize() / tileRowHeight, tileRows.length)">
    <div *cdkVirtualFor="let row of tileRows" class="tile-row">
      @for (content of row; track content) {
        <div title="{{ content.details.name }}" class="content-wrapper"
          (click)="showFileMenu = false; checkContent(content, $event['shiftKey'] ? true : false); $event.stopPropagation()"
          (dblclick)="content.details.type !== 'FILE' ? setActiveDirectory(content) : null"
          [class.checked]="checked.has(content)"
          >
                <!-- invenire-v2's rule: files get a file icon, every other row (tapes, media, sessions, folders) a folder -->
                <i class="icon fas" [ngClass]="content.details.type === 'FILE' ? 'fa-file' : 'fa-folder'"></i>
          <div class='content-name'>{{ content.details.name }}</div>
        </div>
      }
    </div>
  </cdk-virtual-scroll-viewport>
</ng-template>

<!-- Table view for contents -->
<ng-template #tableView>
  <!-- the API sorts the pages of folders and backup sessions; media and the media types are not sortable -->
  @let sortable = activeDirectory.details.type === 'DIRECTORY' || activeDirectory.details.type === 'BACKUP';
  <cdk-virtual-scroll-viewport #viewport class="viewport" [itemSize]="rowHeight"
    (scrolledIndexChange)="onScrolledIndexChange($event, viewport.getViewportSize() / rowHeight, activeDirectory.contents.length)">
  <table class="table table-hover table-sm">
    <!-- keeps the header in view while the virtual scroll moves the rows -->
    <thead [style.top.px]="-viewport.getOffsetToRenderedContentStart()">
      <tr>
        <th class="no-sort checkbox">
          <input type="checkbox" title="Select all loaded"
            [checked]="activeDirectory.contents.length > 0 && checkedContent.length === activeDirectory.contents.length"
            (click)="checkAll(); $event.stopPropagation()"
            >
        </th>
        <th class="no-sort" [style.width.px]="26"><i title="Tagged?" class="fas fa-tag"></i></th>
        <th scope="col"
          [class.no-sort]="!sortable"
          [ngClass]="sortable && currentSortingMethod === 'NAME' ? 'fa-sort-' + currentSortingDirection.toLowerCase() : null"
          (click)="sortable && changeSortingMethod('NAME'); $event.stopPropagation()"
          [style.width.%]="activeDirectory.details.type === 'MEDIA' ? 100 : undefined">
          <i class="table-icon fas fa-folder"></i>
          Name
        </th>

        @if (activeDirectory.details.type !== 'MEDIA' && activeDirectory.details.type !== 'PARENT') {
          <th class='date-col no-sort' scope="col">
            Created
          </th>
          <th class='date-col no-sort' scope="col">
            Accessed
          </th>
          <th class='date-col' scope="col"
            [ngClass]="currentSortingMethod === 'MODIFY_DATE' ? 'fa-sort-' + currentSortingDirection.toLowerCase() : null"
            (click)="changeSortingMethod('MODIFY_DATE'); $event.stopPropagation()">
            Modified
          </th>
          <th class="last-col" scope="col"
            [ngClass]="currentSortingMethod === 'SIZE' ? 'fa-sort-' + currentSortingDirection.toLowerCase() : null"
            (click)="changeSortingMethod('SIZE'); $event.stopPropagation()">
            Size
          </th>
          <th class="col-sm no-sort" scope="col">
            <i title="Deleted?" class="fas fa-trash"></i>
          </th>
        }

        @if (activeDirectory.details.type === 'MEDIA') {
          <th class='date-col no-sort' scope="col">
            Date
          </th>
          <th class="last-col no-sort" scope="col">
            Time
          </th>
          <th class="last-col no-sort" scope="col">
            Size
          </th>
        }
      </tr>
    </thead>
    <tbody>
      <!-- rows read fields from several InvDataDetails variants (file, media, backup), so the row is left untyped -->
      <tr *cdkVirtualFor="let content of $any(activeDirectory.contents)" class='content-wrapper-row' title="{{ content.details.name }}"
        (click)="viewContentDetails(content); $event.stopPropagation()"
        (dblclick)="content.details.type !== 'FILE' ? setActiveDirectory(content) : null"
        [class.checked]="checked.has(content)"
        [class.selected]="selectedDataDetails && selectedDataDetails.id === content.details.id"
        >
        <td class="checkbox"><input type="checkbox"  [checked]="checked.has(content)" (click)="checkContent(content, true); $event.stopPropagation()" /></td>
        <td>@for (tag of content.details.tags; track tag.id) {
          <i class="fas fa-tag mr-1" [title]="tag.name" [style.color]="tag.color"></i>
        } @empty {
          @if (content.details.tagged) {
            <i title="tagged" class="fas fa-tag"></i>
          }
        }</td>
                  <td class="name" title="{{ content.details.name }}"><i class="table-icon fas" [ngClass]="content.details.type === 'FILE' ? 'fa-file' : 'fa-folder'"></i>{{ content.details.name || '{inv-unknown}' }}</td>
        @if (activeDirectory.details.type !== 'MEDIA' && activeDirectory.details.type !== 'PARENT') {
          <td class='date-col' [title]="secondsToMilliseconds(content.details.createDate) | date:'short'">{{ secondsToMilliseconds(content.details.createDate) | date:'shortDate' }}</td>
          <td class='date-col' [title]="secondsToMilliseconds(content.details.accessDate) | date:'short'">{{ secondsToMilliseconds(content.details.accessDate) | date:'shortDate' }}</td>
          <td class='date-col' [title]="secondsToMilliseconds(content.details.modifyDate) | date:'short'">{{ secondsToMilliseconds(content.details.modifyDate) | date:'shortDate' }}</td>
          <td ngPreserveWhitespaces class="last-col">{{ content.details.type === 'FILE' || content.details.type === 'DIRECTORY' ? formatBytes(content.details.size) : '' }}</td>
          <td class="col-sm" scope="col">
            @if (content.details.type === 'FILE' && content.details.deleted) {
              <i title="deleted" class="fas fa-trash td-trash-icon"></i>
            }
          </td>
        }
        @if (activeDirectory.details.type === 'MEDIA') {
          <td class='date-col' [title]="secondsToMilliseconds(content.details.backupDate) | date:'shortDate'">{{ secondsToMilliseconds(content.details.backupDate) | date:'shortDate' }}</td>
          <td class='date-col' [title]="secondsToMilliseconds(content.details.backupDate) | date:'shortTime'">{{ secondsToMilliseconds(content.details.backupDate) | date:'shortTime' }}</td>
          <td ngPreserveWhitespaces class="last-col">{{ formatBytes(content.details.size) }}</td>
        }
      </tr>
      @if (activeDirectory.contents.length === 0) {
        <tr>
          <td [style.font-size.rem]="1.5" colspan="5">No content</td>
        </tr>
      }
    </tbody>
  </table>
  </cdk-virtual-scroll-viewport>
</ng-template>
